import { addDays, differenceInHours, formatISO, format, parseISO } from 'date-fns';
import { v4 as uuidv4 } from 'uuid';

import MobileDetect from './device';

const isMobile = MobileDetect.mobile();
const isInternetExplorer = /MSIE|Trident/.test(navigator.userAgent);

export interface CalendarEvent {
  allDay: boolean;
  start: Date;
  end?: Date;
  title: string;
  description: string;
  location: string;
  coordinates: string;
}

export enum CalendarType {
  Apple = 'apple',
  Google = 'google',
  Outlook = 'outlook',
  OutlookWeb = 'outlook-web',
}

interface CalendarEventFormatted {
  allDay: string;
  start: string;
  end: string;
  duration: string;
  title: string;
  description: string;
  location: string;
  coordinates: string;
}

const timeZoneNameFromDate = (dateString: string): string => {
  const timeZoneString = parseISO(dateString).toLocaleDateString('en-US', {
    timeZoneName: 'short',
  });
  const timeZoneName = timeZoneString.split(' ').pop() || '';
  return timeZoneName;
};

const formatDateTimeForICalendar = (dateString: string): string => {
  return format(parseISO(dateString), "yyyyMMdd'T'HHmmss");
};

// https://github.com/InteractionDesignFoundation/add-event-to-calendar-docs/blob/master/services/google.md
const googleCalendarUrl = (calendarEvent: CalendarEventFormatted): string =>
  'https://calendar.google.com/calendar/render' +
  '?action=TEMPLATE' +
  `&text=${calendarEvent.title}` +
  `&dates=${calendarEvent.start}/${calendarEvent.end}` +
  `&details=${calendarEvent.description}` +
  `&location=${calendarEvent.location}`;

// https://github.com/InteractionDesignFoundation/add-event-to-calendar-docs/blob/master/services/outlook-web.md
const outlookWebCalendarUrl = (calendarEvent: CalendarEventFormatted): string =>
  'https://outlook.live.com/owa/' +
  '?path=/calendar/action/compose' +
  '&rru=addevent' +
  `&startdt=${calendarEvent.start}` +
  `&enddt=${calendarEvent.end}` +
  `&subject=${calendarEvent.title}` +
  `&allday=${calendarEvent.allDay}` +
  `&body=${calendarEvent.description}` +
  `&location=${calendarEvent.location}`;

// https://gist.github.com/DeMarko/6142417
const icsCalendarUrl = (calendarEvent: CalendarEventFormatted): string => {
  const content = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'BEGIN:VEVENT',
    `DTSTAMP:${formatISO(Date.now(), { format: 'basic' })}`,
    'STATUS:CONFIRMED',
    `UID:${uuidv4()}`,
    'SEQUENCE:0',
    `DTSTART;TZID=${timeZoneNameFromDate(calendarEvent.start)}:${formatDateTimeForICalendar(calendarEvent.start)}`,
    `DTEND;TZID=${timeZoneNameFromDate(calendarEvent.end)}:${formatDateTimeForICalendar(calendarEvent.end)}`,
    `SUMMARY:${calendarEvent.title}`,
    `DESCRIPTION:${calendarEvent.description}`,
    `X-ALT-DESC;FMTTYPE=text/html:${calendarEvent.description}`,
    `GEO:${calendarEvent.coordinates}`,
    `LOCATION:${calendarEvent.location}`,
    'BEGIN:VALARM',
    'ACTION:DISPLAY',
    'END:VALARM',
    'TRANSP:OPAQUE',
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\n');

  if (isMobile) {
    return `data:text/calendar;charset=utf8,${encodeURIComponent(content)}`;
  } else {
    return content;
  }
};

export const buildCalendarUrl = (calendarEvent: CalendarEvent, type: CalendarType): string => {
  // https://stackoverflow.com/a/30249034/5207233
  // DTSTART must exclude time data if the event is all day
  const formatIsoRepresentation = calendarEvent.allDay ? 'date' : 'complete';
  type FormatIsoOptions = Parameters<typeof formatISO>[1];
  const formatIsoOptions: FormatIsoOptions = { format: 'basic', representation: formatIsoRepresentation };

  const calendarEventStart = calendarEvent.start && formatISO(calendarEvent.start, formatIsoOptions);
  const calendarEventStartPlusOneDay =
    calendarEvent.start && formatISO(addDays(calendarEvent.start, 1), formatIsoOptions);
  const calendarEventEnd = calendarEvent.end && formatISO(calendarEvent.end, formatIsoOptions);
  const calendarEventDuration = calendarEvent.end && calendarEvent.start && differenceInHours(calendarEvent.end, calendarEvent.start).toString();

  const icsData: CalendarEventFormatted = {
    allDay: calendarEvent.allDay ? 'true' : 'false',
    start: calendarEventStart || '',
    end: (calendarEvent.allDay ? calendarEventStartPlusOneDay : calendarEventEnd) || '',
    duration: calendarEventDuration || '',
    title: calendarEvent.title || '',
    description: calendarEvent.description || '',
    location: calendarEvent.location || '',
    coordinates: calendarEvent.coordinates || '',
  };

  const urlData: CalendarEventFormatted = Object.entries(icsData).reduce((obj, [key, value]) => {
    obj[key as keyof CalendarEventFormatted] = encodeURIComponent(value);
    return obj;
  }, {} as CalendarEventFormatted);

  switch (type) {
    case CalendarType.Apple:
      return icsCalendarUrl(icsData);
    case CalendarType.Google:
      return googleCalendarUrl(urlData);
    case CalendarType.Outlook:
      return icsCalendarUrl(icsData);
    case CalendarType.OutlookWeb:
      return outlookWebCalendarUrl(urlData);
    default:
      break;
  }
  return '';
};

export const openCalendarUrl = (url: string) => {
  const urlIsIcs = url.startsWith('data') || url.startsWith('BEGIN');
  if (isMobile || !urlIsIcs) {
    window.open(url, '_blank');
  } else {
    const filename = 'download.ics';
    const blob = new Blob([url], { type: 'text/calendar;charset=utf-8' });
    if (isInternetExplorer) {
      (window.navigator as { msSaveOrOpenBlob?: (blob: Blob, filename: string) => void }).msSaveOrOpenBlob?.(blob, filename);
    } else {
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }
};
